<script lang="ts" setup>
import { ref, computed, Ref, onBeforeMount, onBeforeUnmount, inject, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { KeyValue, KeyValuePair } from '@/helpers/Interfaces';
import { FormEntry } from '../form';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { CompanyEntry, CompanyType, instaceOfCompanyEntry } from '.';
import DataSourcesService from '@/modules/studio/data-sources/services/DataSourcesService';
import properties from '../../properties';
import { Definition as RegisterDefinition, RegisterType } from '../logito-register';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger, useDynamicTriggerChannelId } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'company-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null
});

const getDynamicTriggerChannelId = useDynamicTriggerChannelId(() => props.form.blueprint.id);

const { $events } = useEvents();
const level = inject<number>('level', 0);
const entryData = ref(new CompanyEntry()) as Ref<CompanyEntry>;
const defaultValue = ref(null) as Ref<KeyValuePair>;
const defaultValueOptions = ref([]) as Ref<KeyValuePair[]>;

const triggerComponentCtx = createTriggerComponentCtx(
    {

    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instaceOfCompanyEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        return value;
    },
    set(value: KeyValue<string, string> | null)
    {
        entryData.value.data = value;
        useDynamicTrigger($events, getDynamicTriggerChannelId()).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    },
});

const blueprint = computed(() => props.blueprint);
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
const design = computed(() => props.form.designMode());
const endpoint = computed(() =>
{
    if (design.value) return null;

    if (props.entry.fieldIndex)
    {
        return `modules/${props.entry.moduleId}/table-templates/select-options/${props.entry.fieldIndex}/${blueprint.value.name}`;
    }

    return `simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${blueprint.value.name}`;
});
const isStart = computed(() => props.entry.actionName === 'bpmn-start');
const isLinkedWithRegister = computed(() =>
{
    const linkedRegisters = props.form.schema
        .components(RegisterDefinition.type)
        .filter((p) => (p as RegisterType).bindWith == props.blueprint.name);

    return linkedRegisters.any();
});

const fetchDefaultCompany = async (): Promise<void> =>
{
    if (blueprint.value.defaultValue != null)
    {
        if (design.value)
        {
            const result = await DataSourcesService.fetchDefaultCompanySelect(blueprint.value.defaultValue);

            if (result.length > 0)
                defaultValue.value = result[0];
        }
        else if (value.value == null)
        {
            const result = await DataSourcesService.fetchDefaultCompanySelect(blueprint.value.defaultValue);

            if (result.length > 0)
                value.value = result[0];
        }
    }
};

const fetchDefaultCompanyOptions = async (): Promise<void> =>
{
    if (design.value)
    {
        const result = await DataSourcesService.fetchDefaultCompanySelect();

        defaultValueOptions.value = result;
    }
};

const onDefaultValueUpdate = (): void =>
{
    if (defaultValue.value != null)
    {
        blueprint.value.defaultValue = defaultValue.value.key;
    }
};

fetchDefaultCompanyOptions();
fetchDefaultCompany();
</script>

<template>
    <form-component-wrapper class="company-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <ideo-select
                    v-model="value"
                    :id="blueprint.id"
                    :disabled="readonly"
                    :placeholder="placeholder"
                    :endpoint="endpoint"
                    :data-test="blueprint.id"
                    :preselect-when-one-option="isStart"
                    :narrow-options="{ linked: isLinkedWithRegister }"
                />
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <ideo-form-group :label="$t('[[[Wartość domyślna]]]')">
                        <ideo-select
                            v-model="defaultValue"
                            :options="defaultValueOptions"
                            @update:modelValue="onDefaultValueUpdate()"
                        />
                    </ideo-form-group>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-error :form="form" :blueprint="blueprint" :label="$t('[[[Niestandardowy błąd]]]')" />
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
